/* ==========================================================================
   VoxTune — Product Data Layer
   One centralized, normalized source for products, variants, "programs"
   (DAW options derived from a configurable variant option), collections,
   audio samples, reviews and upsell relationships.

   Hydration is dynamic: Liquid renders product JSON into
   <script type="application/json" data-voxtune-products> islands (one per
   section that needs data). This module merges them, so NO products are ever
   hardcoded — everything originates from Shopify.

   Registered as a VoxTune module: VoxTune.use('data')
   ========================================================================== */
(function (w) {
  'use strict';
  var VoxTune = w.VoxTune;
  if (!VoxTune) { console.error('[VoxTune] experience-manager.js must load before product-data-layer.js'); return; }

  VoxTune.module('data', function (VT) {
    var byId = Object.create(null);      // product id -> product
    var byHandle = Object.create(null);  // handle -> product
    var collections = Object.create(null); // handle -> [product ids]

    // Which variant option name represents the "program"/DAW (configurable).
    var PROGRAM_OPTION = (VT.shop && VT.shop.programOption) || 'Program';

    function normalizeVariant(v) {
      return {
        id: v.id,
        title: v.title,
        price: v.price,
        compareAt: v.compare_at_price || v.compareAt || null,
        available: v.available !== false,
        options: v.options || [],
        optionValues: v.option_values || v.optionValues || null,
        sku: v.sku || '',
        featuredImage: (v.featured_image && v.featured_image.src) || v.image || null
      };
    }

    function programOptionIndex(p) {
      var names = p.optionNames || [];
      for (var i = 0; i < names.length; i++) {
        if (String(names[i]).toLowerCase() === String(PROGRAM_OPTION).toLowerCase()) return i;
      }
      return -1; // no program option on this product
    }

    function ingest(p) {
      if (!p || p.id == null) return;
      var existing = byId[p.id];
      var product = existing || {
        id: p.id, handle: p.handle, title: p.title,
        url: p.url, price: p.price, compareAt: p.compare_at_price || null,
        optionNames: p.options || p.optionNames || [],
        variants: [], images: p.images || [], featuredImage: p.featured_image || p.featuredImage || null,
        audio: { before: null, after: null }, reviews: [], meta: p.meta || {}
      };
      if (p.variants) product.variants = p.variants.map(normalizeVariant);
      // before/after audio + extra data arrive via metafields rendered by Liquid
      if (p.audio) product.audio = Object.assign(product.audio, p.audio);
      if (p.reviews) product.reviews = p.reviews;
      if (p.meta) product.meta = Object.assign(product.meta || {}, p.meta);

      byId[product.id] = product;
      if (product.handle) byHandle[product.handle] = product;
      return product;
    }

    function ingestCollection(handle, productIds) {
      if (!handle) return;
      collections[handle] = (collections[handle] || []).concat(productIds || []);
    }

    /* ---- public lookups ------------------------------------------------- */
    function get(idOrHandle) { return byId[idOrHandle] || byHandle[idOrHandle] || null; }
    function all() { return Object.keys(byId).map(function (k) { return byId[k]; }); }
    function inCollection(handle) {
      return (collections[handle] || []).map(get).filter(Boolean);
    }

    /* ---- "programs" (DAW options) -------------------------------------- */
    // Returns only the program values actually available for a product.
    function programsFor(idOrHandle) {
      var p = get(idOrHandle); if (!p) return [];
      var idx = programOptionIndex(p);
      if (idx < 0) return [];
      var seen = Object.create(null); var out = [];
      p.variants.forEach(function (v) {
        if (!v.available) return;
        var val = v.options[idx];
        if (val && !seen[val]) { seen[val] = 1; out.push({ name: val, variant: v }); }
      });
      return out;
    }

    // Find the variant of `product` that matches a chosen program name.
    function variantForProgram(idOrHandle, programName) {
      var p = get(idOrHandle); if (!p) return null;
      var idx = programOptionIndex(p);
      if (idx < 0) return p.variants[0] || null;
      var match = p.variants.filter(function (v) {
        return v.available && String(v.options[idx]).toLowerCase() === String(programName).toLowerCase();
      })[0];
      return match || null;
    }

    /* ---- upsells: exclude what's in cart, keep program synced ----------- */
    function upsellCandidates(opts) {
      opts = opts || {};
      var source = opts.collection ? inCollection(opts.collection)
        : (opts.products || []).map(get).filter(Boolean);
      var excludeIds = (opts.excludeProductIds || []).reduce(function (m, id) { m[id] = 1; return m; }, {});
      var program = opts.program || null;
      return source.filter(function (p) {
        if (excludeIds[p.id]) return false;                 // duplicate prevention
        if (program && programsFor(p).length) {             // only if the program exists
          return !!variantForProgram(p, program);
        }
        return true;
      }).slice(0, opts.limit || 99);
    }

    /* ---- hydrate from DOM JSON islands --------------------------------- */
    function hydrateFromDOM(root) {
      (root || document).querySelectorAll('script[type="application/json"][data-voxtune-products]').forEach(function (el) {
        if (el.dataset.vtConsumed) return;
        el.dataset.vtConsumed = '1';
        try {
          var json = JSON.parse(el.textContent || '{}');
          (json.products || []).forEach(ingest);
          (json.collections || []).forEach(function (c) { ingestCollection(c.handle, c.product_ids); });
        } catch (e) { console.error('[VoxTune] data island parse error', e); }
      });
      VT.emit('data:hydrated', { count: all().length });
    }

    VT.ready(function () { hydrateFromDOM(document); });
    // Sections injected later (Theme Editor) can re-hydrate.
    document.addEventListener('shopify:section:load', function (e) { hydrateFromDOM(e.target); });

    return {
      ingest: ingest, ingestCollection: ingestCollection,
      get: get, all: all, inCollection: inCollection,
      programsFor: programsFor, variantForProgram: variantForProgram,
      upsellCandidates: upsellCandidates,
      hydrateFromDOM: hydrateFromDOM,
      get programOption() { return PROGRAM_OPTION; }
    };
  });

  // Eagerly boot the data layer (cheap; just registers + hydrates on ready).
  VoxTune.use('data');
})(window);
